import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Pencil, RefreshCw, Sparkles, Star } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import React, { useState } from "react";
import { AuthRequired } from "../../components/AuthRequired";
import { PageLayout } from "../../components/PageLayout";
import { BrandedSpinner } from "../../components/ui/branded-spinner";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { DailyProgressIndicator } from "../../components/ui/daily-progress-indicator";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigation } from "../../contexts/NavigationContext";
import { useToast } from "../../hooks/useToast";
import { useUserAccount } from "../../hooks/useUserAccount";
import { apiClient } from "../../lib/api";
import { getErrorInfo, type ApiError } from "../../lib/errorUtils";

export const Route = createFileRoute("/_layout/story-mode")({
    component: StoryMode,
});

// Genre options for Plus users
const GENRES = [
    { id: "mixed", name: "Mixed", icon: "🎭", description: "Variety of different themes" },
    { id: "Adventure", name: "Adventure", icon: "🗺️", description: "Exciting journeys and discoveries" },
    { id: "Fantasy", name: "Fantasy", icon: "🦄", description: "Magic and mythical creatures" },
    { id: "Animals", name: "Animals", icon: "🐨", description: "Animal friends and habitats" },
    { id: "Science", name: "Science", icon: "🔬", description: "Experiments and inventions" },
    { id: "Everyday", name: "Everyday", icon: "🏠", description: "Family and daily adventures" },
    { id: "Magic", name: "Magic", icon: "✨", description: "Wizards and magical objects" },
    { id: "Space", name: "Space", icon: "🚀", description: "Planets and space exploration" },
] as const;

// Fallback topics for when quota is exceeded
const getFallbackTopics = (genre = "mixed"): string[] => {
    const fallbackTopics: Record<string, string[]> = {
        Adventure: ["Treasure Island Quest", "Mountain Climbing Fun", "Forest Explorer", "Cave Discovery"],
        Fantasy: ["Magic Castle", "Friendly Dragon", "Fairy Garden", "Wizard School"],
        Animals: ["Safari Adventure", "Ocean Friends", "Farm Animals", "Pet Rescue"],
        Science: ["Robot Helper", "Space Station", "Invention Lab", "Weather Machine"],
        Everyday: ["Family Picnic", "Birthday Party", "School Day", "Neighborhood Fun"],
        Magic: ["Magic Wand", "Spell Book", "Potion Making", "Magic Show"],
        Space: ["Rocket Ship", "Planet Explorer", "Alien Friends", "Space Station"],
        mixed: ["Space Adventure", "Magical Forest", "Underwater World", "Flying Castle"],
    };

    return fallbackTopics[genre];
};

function StoryMode() {
    const { user, loading } = useAuth();
    const { navigateToStory } = useNavigation();
    const queryClient = useQueryClient();
    const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
    const [selectedGenre, setSelectedGenre] = useState<string>("mixed");
    const [customTopic, setCustomTopic] = useState<string>("");
    const [isCustomMode, setIsCustomMode] = useState<boolean>(false);
    const { toast } = useToast();
    const { isPremium } = useUserAccount();
    const posthog = usePostHog();

    const {
        data: rawTopics,
        isLoading: isLoadingTopics,
        isFetching: isFetchingTopics,
        error: topicsError,
        refetch: refetchTopics,
    } = useQuery({
        queryKey: ["story-topics", selectedGenre],
        queryFn: () => apiClient.generateStoryTopics(selectedGenre === "mixed" ? undefined : selectedGenre),
        enabled: !!user && !selectedTopic,
        retry: 2,
        staleTime: 3 * 60 * 1000, // 3 minutes
    });

    // Use fallback topics when there's any error, otherwise use fetched topics
    const topics = topicsError ? getFallbackTopics(selectedGenre) : (rawTopics ?? []);

    React.useEffect(() => {
        if (topicsError) {
            const errorInfo = getErrorInfo(topicsError as ApiError);
            toast({
                title: errorInfo.title,
                description: errorInfo.message,
                variant: errorInfo.type === "generic" ? "destructive" : "destructive",
            });
        }
    }, [topicsError, toast]);

    const storyMutation = useMutation({
        mutationFn: (topic: string) => apiClient.createStory(topic),
        onSuccess: (data) => {
            // Track story started event for analytics
            posthog.capture("story_started", {
                story_id: data.story.id,
                topic: data.story.topic,
                genre: selectedGenre,
                is_custom_topic: isCustomMode,
                timestamp: new Date().toISOString(),
            });

            // Invalidate quota status to refresh usage
            queryClient.invalidateQueries({ queryKey: ["quota-status"] });
            navigateToStory(data.story.id);
        },
        onError: (error: ApiError) => {
            const errorInfo = getErrorInfo(error);
            toast({
                title: errorInfo.title,
                description: errorInfo.message,
                variant: "destructive",
            });
            setSelectedTopic(null);
        },
    });

    const selectTopic = (topic: string) => {
        setSelectedTopic(topic);
        storyMutation.mutate(topic);
    };

    const handleCustomTopicSubmit = () => {
        if (customTopic.trim().length > 0 && customTopic.trim().length <= 50) {
            selectTopic(customTopic.trim());
            setCustomTopic("");
            setIsCustomMode(false);
        }
    };

    const isLoadingOrFetching = isLoadingTopics || isFetchingTopics;
    const isGeneratingStory = storyMutation.isPending;

    const hasTopicsError = !!topicsError;

    // Show loading state while auth is being determined
    if (loading) {
        return (
            <PageLayout title="Story Mode">
                <div className="flex items-center justify-center min-h-96">
                    <div className="p-8 max-w-md">
                        <div className="text-center space-y-4">
                            <div className="h-12 w-12 bg-muted animate-pulse rounded mx-auto"></div>
                            <div className="h-8 w-32 bg-muted animate-pulse rounded mx-auto"></div>
                            <div className="h-4 w-48 bg-muted animate-pulse rounded mx-auto"></div>
                            <div className="h-10 w-24 bg-muted animate-pulse rounded mx-auto"></div>
                        </div>
                    </div>
                </div>
            </PageLayout>
        );
    }

    if (!user) {
        return (
            <PageLayout title="Story Mode">
                <AuthRequired
                    icon={<Sparkles className="h-12 w-12 text-primary mx-auto" />}
                    description="Please sign in to start your story adventure!"
                />
            </PageLayout>
        );
    }

    return (
        <PageLayout title="Story Mode" breadcrumbs={[{ label: "Home", to: "/" }, { label: "Create Story" }]}>
            <main className="container mx-auto px-4 sm:px-6 py-8">
                <div className="max-w-4xl mx-auto space-y-8">
                    <div className="text-center">
                        <h2 className="text-4xl font-bold text-primary flex items-center justify-center gap-3">
                            <div className="w-8 h-8 md:w-12 md:h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                <Sparkles className="h-4 w-4 md:h-6 md:w-6" />
                            </div>
                            Start Your Story
                        </h2>
                    </div>

                    <div className="text-center">
                        <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                            Pick a story topic and let's create an amazing adventure together! Your drawings will help
                            shape the story.
                        </p>
                    </div>

                    <DailyProgressIndicator className="max-w-2xl mx-auto" />

                    {/* Genre Selection - Plus Feature */}
                    {isPremium && !isCustomMode && (
                        <Card className="max-w-2xl mx-auto p-4 border-primary/20 bg-primary/5">
                            <div className="space-y-4">
                                <div className="flex items-center gap-2">
                                    <Star className="h-5 w-5 text-secondary" />
                                    <h3 className="text-lg font-semibold text-secondary">Choose Your Story Genre</h3>
                                    <span className="text-xs px-2 py-1 bg-[hsl(var(--secondary))]/20 text-secondary rounded-full">
                                        Plus
                                    </span>
                                </div>

                                <div className="grid grid-cols-4 md:grid-cols-4 gap-3">
                                    {GENRES.map((genre) => (
                                        <Button
                                            key={genre.id}
                                            variant={selectedGenre === genre.id ? "default" : "outline"}
                                            size="sm"
                                            className="h-auto p-2 flex flex-col items-center gap-1"
                                            onClick={() => setSelectedGenre(genre.id)}
                                            disabled={isGeneratingStory}
                                        >
                                            <span className="text-base">{genre.icon}</span>
                                            <span className="text-xs">{genre.name}</span>
                                        </Button>
                                    ))}
                                </div>

                                <div className="text-center">
                                    <p className="text-sm text-muted-foreground">
                                        {GENRES.find((g) => g.id === selectedGenre)?.description}
                                    </p>
                                </div>
                            </div>
                        </Card>
                    )}

                    {/* Custom Topic Mode */}
                    {isPremium && isCustomMode && (
                        <Card className="max-w-2xl mx-auto p-4 border-secondary/20 bg-secondary/5">
                            <div className="space-y-4">
                                <div className="flex items-center gap-2">
                                    <Pencil className="h-5 w-5 text-secondary" />
                                    <h3 className="text-lg font-semibold text-secondary">Create Your Own Topic</h3>
                                    <span className="text-xs px-2 py-1 bg-[hsl(var(--secondary))]/20 text-secondary rounded-full">
                                        Plus
                                    </span>
                                </div>

                                <div className="space-y-3">
                                    <Label htmlFor="custom-topic">Your Story Topic (up to 50 characters)</Label>
                                    <Input
                                        id="custom-topic"
                                        value={customTopic}
                                        onChange={(e) => setCustomTopic(e.target.value)}
                                        placeholder="e.g., A robot who loves to bake cookies"
                                        maxLength={50}
                                        disabled={isGeneratingStory}
                                    />
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-muted-foreground">
                                            {customTopic.length}/50 characters
                                        </span>
                                        <div className="flex gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    setIsCustomMode(false);
                                                    setCustomTopic("");
                                                }}
                                                disabled={isGeneratingStory}
                                            >
                                                Back to Topics
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={handleCustomTopicSubmit}
                                                disabled={
                                                    isGeneratingStory ||
                                                    customTopic.trim().length === 0 ||
                                                    customTopic.trim().length > 50
                                                }
                                            >
                                                {isGeneratingStory ? "Creating..." : "Create Story"}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    )}

                    {/* Topic Selection - Only show when not in custom mode */}
                    {!isCustomMode && (
                        <div className="space-y-6">
                            {hasTopicsError && (
                                <Card className="p-4 border-destructive/20 bg-destructive/5">
                                    <div className="text-center">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => refetchTopics()}
                                            disabled={isFetchingTopics}
                                            className="gap-2 hover:text-primary hover:border-primary"
                                        >
                                            <RefreshCw
                                                className={`h-4 w-4 ${isFetchingTopics ? "animate-spin" : ""}`}
                                            />
                                            {isFetchingTopics ? "Retrying..." : "Try Again"}
                                        </Button>
                                    </div>
                                </Card>
                            )}

                            <div className="flex items-center justify-center gap-3 max-w-md mx-auto">
                                {!hasTopicsError && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => refetchTopics()}
                                        disabled={isLoadingOrFetching || isGeneratingStory}
                                        className="gap-2 hover:text-primary hover:border-primary flex-1"
                                    >
                                        <RefreshCw className={`h-4 w-4 ${isFetchingTopics ? "animate-spin" : ""}`} />
                                        {isFetchingTopics ? "Getting New Topics..." : "Get New Topics"}
                                    </Button>
                                )}

                                {isPremium && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setIsCustomMode(true)}
                                        disabled={isGeneratingStory}
                                        className="gap-2 text-secondary hover:border-secondary flex-1"
                                    >
                                        <Pencil className="h-4 w-4" />
                                        Create Your Own
                                    </Button>
                                )}
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                                {isLoadingOrFetching
                                    ? Array.from({ length: 4 }).map((_, index) => (
                                          <Card key={`skeleton-${index}`} className="p-4 border-2">
                                              <div className="text-center space-y-3">
                                                  <div className="h-6 bg-primary/10 rounded mx-4 animate-pulse"></div>
                                                  <div className="h-6 bg-primary/10 rounded mx-4 animate-pulse"></div>
                                              </div>
                                          </Card>
                                      ))
                                    : (topics as string[]).map((topic: string, index: number) => (
                                          <Card
                                              key={index}
                                              className={`p-4 transition-all border-2 ${
                                                  isGeneratingStory
                                                      ? "opacity-50 cursor-not-allowed border"
                                                      : "hover:shadow-lg cursor-pointer hover:border-primary/30"
                                              } ${selectedTopic === topic ? "border-primary bg-primary/5" : ""}`}
                                              onClick={() => !isGeneratingStory && selectTopic(topic)}
                                          >
                                              <div className="text-center py-4">
                                                  {isGeneratingStory && selectedTopic === topic ? (
                                                      <div className="flex items-center justify-center gap-2">
                                                          <BrandedSpinner size="sm" animation="rotate" speed="fast" />
                                                          <span className="text-sm text-muted-foreground">
                                                              Creating story...
                                                          </span>
                                                      </div>
                                                  ) : (
                                                      <h3 className="text-lg font-semibold">{topic}</h3>
                                                  )}
                                              </div>
                                          </Card>
                                      ))}
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </PageLayout>
    );
}
